import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  subscribeToConnections, subscribeToPendingRequests,
  acceptConnection, declineConnection, getUserProfile, searchUsers,
} from "../services/userService";

export default function ConnectionsScreen({ onOpenChat, onOpenProfile, onNotif }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("connections");
  const [connections, setConnections] = useState([]);
  const [requests, setRequests] = useState([]);
  const [connProfiles, setConnProfiles] = useState({});
  const [reqProfiles, setReqProfiles] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!user || user.isGuest) return;
    const unsubConn = subscribeToConnections(user.uid, async (conns) => {
      setConnections(conns);
      const profiles = {};
      await Promise.all(conns.map(async (c) => {
        const p = await getUserProfile(c.otherUid);
        if (p) profiles[c.otherUid] = p;
      }));
      setConnProfiles(profiles);
    });
    const unsubReq = subscribeToPendingRequests(user.uid, async (reqs) => {
      setRequests(reqs);
      const profiles = {};
      await Promise.all(reqs.map(async (r) => {
        const p = await getUserProfile(r.from);
        if (p) profiles[r.from] = p;
      }));
      setReqProfiles(profiles);
    });
    return () => { unsubConn(); unsubReq(); };
  }, [user]);

  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    const delay = setTimeout(async () => {
      setSearching(true);
      const results = await searchUsers(searchQuery);
      setSearchResults(results.filter(u => u.uid !== user?.uid));
      setSearching(false);
    }, 400);
    return () => clearTimeout(delay);
  }, [searchQuery]);

  const handleAccept = async (req) => {
    await acceptConnection(req.from, user.uid);
    onNotif("Connection accepted ✓");
  };

  const handleDecline = async (req) => {
    await declineConnection(req.from, user.uid);
    onNotif("Request declined");
  };

  if (!user || user.isGuest) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0A0A0A", padding: 40, textAlign: "center", fontFamily: "'DM Sans', sans-serif" }}>
        <div>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🤝</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#ffffff", marginBottom: 8, letterSpacing: -0.3 }}>Sign in to connect</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>Create an account to connect with others and build your friends list.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0A0A0A", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        .conn-header { padding: 80px 40px 0; max-width: 900px; margin: 0 auto; text-align: center; }
        .conn-title { font-family: 'DM Serif Display', serif; font-size: clamp(32px, 4vw, 48px); color: #ffffff; letter-spacing: -2px; margin-bottom: 20px; }
        .conn-tabs { display: flex; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 4px; margin: 0 auto 20px; max-width: 500px; }
        .conn-tab { flex: 1; padding: 9px; border-radius: 9px; border: none; background: none; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.4); cursor: pointer; transition: all 0.2s; position: relative; }
        .conn-tab:active { transform: scale(0.97); }
        .conn-tab.active { background: rgba(255,255,255,0.1); color: #ffffff; }
        .conn-badge { position: absolute; top: 4px; right: 4px; background: #ff6b6b; color: #ffffff; font-size: 9px; font-weight: 700; border-radius: 100px; padding: 1px 5px; min-width: 16px; text-align: center; }
        .conn-search { position: relative; margin: 0 auto 20px; max-width: 720px; width: calc(100% - 80px); }
        .conn-search-input { width: 100%; padding: 14px 16px 14px 40px; border-radius: 12px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); font-family: 'DM Sans', sans-serif; font-size: 14px; color: #ffffff; outline: none; transition: all 0.2s; }
        .conn-search-input:focus { border-color: rgba(255,255,255,0.3); }
        .conn-search-input::placeholder { color: rgba(255,255,255,0.3); }
        .conn-list { padding: 24px 40px 40px; display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; max-width: 1100px; margin: 0 auto; }
        .conn-card { display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 20px; transition: all 0.2s; }
        .conn-card:active { background: rgba(255,255,255,0.06); }
        .conn-avatar { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; flex-shrink: 0; background: rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; font-size: 18px; cursor: pointer; overflow: hidden; border: 1px solid rgba(255,255,255,0.1); }
        .conn-name { font-size: 14px; font-weight: 600; color: #ffffff; cursor: pointer; letter-spacing: -0.3px; }
        .conn-meta { font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 2px; }
        .conn-actions { display: flex; gap: 6px; margin-left: auto; flex-shrink: 0; }
        .conn-btn { padding: 8px 14px; border-radius: 100px; border: none; font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .conn-btn:active { transform: scale(0.95); }
        .conn-btn-msg { background: #1DB954; color: #ffffff; }
        .conn-btn-accept { background: #1DB954; color: #ffffff; min-width: 36px; font-weight: 700; }
        .conn-btn-decline { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.5); border: 1px solid rgba(255,255,255,0.1); min-width: 36px; }
        .conn-btn-connect { background: #ffffff; color: #0A0A0A; font-weight: 700; }
        .conn-empty { padding: 60px 20px; text-align: center; }
      `}</style>

      <div className="conn-header">
        <div className="conn-title">Connections</div>
        <div className="conn-tabs">
          <button className={`conn-tab ${tab === "connections" ? "active" : ""}`} onClick={() => setTab("connections")}>
            Friends {connections.length > 0 && `(${connections.length})`}
          </button>
          <button className={`conn-tab ${tab === "requests" ? "active" : ""}`} onClick={() => setTab("requests")}>
            Requests
            {requests.length > 0 && <span className="conn-badge">{requests.length}</span>}
          </button>
          <button className={`conn-tab ${tab === "search" ? "active" : ""}`} onClick={() => setTab("search")}>
            Find People
          </button>
        </div>
      </div>

      {tab === "search" && (
        <div className="conn-search">
          <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)" }}>🔍</span>
          <input
            className="conn-search-input"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>
      )}

      <div className="conn-list">
        {tab === "connections" && (
          connections.length === 0 ? (
            <div className="conn-empty">
              <div style={{ fontSize: 40, marginBottom: 12 }}>🤝</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#ffffff", marginBottom: 6, letterSpacing: -0.3 }}>No connections yet</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>Connect with people you meet at venues</div>
            </div>
          ) : connections.map(conn => {
            const p = connProfiles[conn.otherUid];
            if (!p) return null;
            return (
              <div key={conn.connId} className="conn-card">
                <div className="conn-avatar" onClick={() => onOpenProfile(p.uid)}>
                  {p.photoURL ? <img src={p.photoURL} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : "👤"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="conn-name" onClick={() => onOpenProfile(p.uid)}>{p.displayName || "User"}</div>
                  <div className="conn-meta">Connected</div>
                </div>
                <div className="conn-actions">
                  <button className="conn-btn conn-btn-msg" onClick={() => onOpenChat(p)}>💬</button>
                </div>
              </div>
            );
          })
        )}

        {tab === "requests" && (
          requests.length === 0 ? (
            <div className="conn-empty">
              <div style={{ fontSize: 40, marginBottom: 12 }}>📬</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#ffffff", marginBottom: 6, letterSpacing: -0.3 }}>No pending requests</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>Connection requests will appear here</div>
            </div>
          ) : requests.map(req => {
            const p = reqProfiles[req.from];
            if (!p) return null;
            return (
              <div key={req.id} className="conn-card">
                <div className="conn-avatar" onClick={() => onOpenProfile(p.uid)}>
                  {p.photoURL ? <img src={p.photoURL} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : "👤"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="conn-name" onClick={() => onOpenProfile(p.uid)}>{p.displayName || "User"}</div>
                  <div className="conn-meta">Wants to connect</div>
                </div>
                <div className="conn-actions">
                  <button className="conn-btn conn-btn-accept" onClick={() => handleAccept(req)}>✓</button>
                  <button className="conn-btn conn-btn-decline" onClick={() => handleDecline(req)}>✕</button>
                </div>
              </div>
            );
          })
        )}

        {tab === "search" && (
          searching ? (
            <div style={{ padding: "20px 0", textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: 13 }}>Searching...</div>
          ) : searchResults.length === 0 && searchQuery.length >= 2 ? (
            <div className="conn-empty">
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>No users found for "{searchQuery}"</div>
            </div>
          ) : searchResults.map(u => (
            <div key={u.uid} className="conn-card">
              <div className="conn-avatar" onClick={() => onOpenProfile(u.uid)}>
                {u.photoURL ? <img src={u.photoURL} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : "👤"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="conn-name" onClick={() => onOpenProfile(u.uid)}>{u.displayName || "User"}</div>
                <div className="conn-meta">Public profile</div>
              </div>
              <div className="conn-actions">
                <button className="conn-btn conn-btn-connect" onClick={async () => {
                  const { sendConnectionRequest } = await import("../services/userService");
                  await sendConnectionRequest(user.uid, u.uid);
                  onNotif("Request sent ✓");
                }}>Connect</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
